import * as yup from 'yup'
import { noodlesAddressSchema, noodlesRemittanceInstructionsSchema } from './noodles-order.js'
import { MakeRequiredAndNullable } from './utils.js'

export enum NoodlesComplaintLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, refernce ID, etc
  description = 'description', // verbose description of the item (human parseable)
  unitPrice = 'unitPrice', // price per unit
  itemQty = 'itemQty', // quantity of the item for order
  itemDiscountRate = 'itemDiscountRate', // discount rate for the item
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const noodlesComplaintFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: noodlesAddressSchema.nullable().optional(),
    firstName: yup.string().nullable().optional(),
    lastName: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  customer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: noodlesAddressSchema.nullable().optional(),
    firstName: yup.string().nullable().optional(),
    lastName: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  complaintNumber: yup.string().nullable().optional(),
  referenceNumber: yup.string().nullable().optional(),
  date: yup.string().nullable().optional(),
  externalNote: yup.string().nullable().optional(),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        itemDiscountRate: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  taxRate: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  remittanceInstructions: noodlesRemittanceInstructionsSchema,
  showPaymentLink: yup.string().nullable().optional(),
  showBankDetails: yup.string().nullable().optional(),
  isNet: yup.string().nullable().optional(),
})

export type NoodlesComplaintFileData = yup.InferType<typeof noodlesComplaintFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<NoodlesComplaintFileData>

export const noodlesComplaintFileMappings: RequiredFileData = {
  issuer: {
    companyName: '69d4b358-cb67-425e-8bed-3643697440d8',
    address: {
      addressLine1: '7c9cb7ca-9a48-4701-af45-f13bfea75e74',
      addressLine2: 'da4ee1bb-2302-4d4a-9373-fa45fbd5beb2',
      postalCode: '81372744-7fdc-48f4-9613-70c4934943fb',
      city: 'f99914e8-44b8-4568-b841-50e8c3a24125',
      country: '724ee8cd-9f74-41ac-9003-fe67d6013d7a',
    },
    firstName: '72ae5ce0-dd37-45b4-ae82-eea6fa446945',
    lastName: 'b115c22e-7511-4300-a5a6-448d79ff4c04',
    phoneNumber: '919b6c54-9409-48ad-b6e6-1c7443afada3',
    emailAddress: '036f8a16-0d9c-4cd5-8c36-abeaad99cdb7',
  },
  customer: {
    companyName: '5fb83780-5a9a-41ee-bce5-9d464918a586',
    address: {
      addressLine1: '674e7820-c117-48d3-811e-815bdedf2d84',
      addressLine2: '03074e1c-ce6e-44ca-af62-8f01b7ae6ba6',
      postalCode: '03ea5933-3e7d-4dbd-8b08-76a7ff4c9cc0',
      city: '90a9b357-34cc-4a26-b898-ce7c027cf7e5',
      country: 'b6b81f24-460b-4a8b-9f95-5236c8aecfb6',
    },
    firstName: '0360389d-f9e8-4985-97de-fb242fde6994',
    lastName: '29614a42-d68f-4447-b4e9-88a337e625d7',
    phoneNumber: '0778574a-52c8-4674-ad5e-699675732cdb',
    emailAddress: '17d677cc-ac20-4939-a8d6-51ca82b8873d',
  },
  complaintNumber: '1db42257-7ab2-4078-9e3e-c117e26ebc29',
  referenceNumber: '6cc5070b-fd7d-40f2-956f-f6b408ad2712',
  date: '67dec22f-5e08-4131-868e-05754116e356',
  externalNote: '9b1e673d-9f6d-412b-97ad-73cad8e598bc',
  items: [
    {
      itemNumber: null,
      itemName: '0920b992-a55c-47f6-9979-538e78f0d205',
      description: '93764e3f-c6f7-4f5b-99ea-fb32c38aa11d',
      itemQty: 'c05d9d6d-566a-41be-b2b9-ffc10044d4e7',
      unitPrice: 'b674d408-ddb4-4b35-aa6d-83f81544564c',
      itemDiscountRate: '8931f547-02de-4170-af0c-2d249a41fbb0',
      lineTotal: null,
    },
  ],
  taxRate: '3b32f162-a8a9-462d-9704-c7eb71a62b8b',
  agreement: '111cf0dd-4bd4-4cbb-bd6c-31654788917c',
  instructions: 'c8d734ab-ba01-4917-b31f-2bedab640e53',

  remittanceInstructions: {
    bankName: '8ccf34da-ff35-417f-92f8-79c330fb47b1',
    bankAddress: {
      addressLine1: 'ce50755e-4a57-4241-93c0-06197ae35d94',
      addressLine2: '6e8aa0d0-6152-41e6-a8e9-8dadc9f267cd',
      postalCode: '295d6dda-402c-4cdd-a9e9-4e7bec2395f2',
      city: '7494155a-745f-41ef-bb73-82dc39a07bd6',
      country: '4c0e7f61-4dcc-4d06-89e7-4fd4a1a74eb6',
    },
    kto: '5dcb3c09-9816-4bb8-bb88-bc828fe584b5',
    blz: '369ecbc7-807b-44d0-bcf0-eb71fe4ffce0',
    iban: '962c3845-5860-43ca-8b9c-fb0c84093703',
    swift: '11701d8e-f998-4349-a80a-69c3a912f530',
    firstName: 'e83b976c-440f-408c-b92c-4455e6f39f5c',
    lastName: '23758832-f975-46a9-81d4-860d9e13e672',
    ustIdNr: '2f5e1677-6694-4d5a-a957-70b7d707da23',
    beneficiaryName: '20cab899-f601-4f4b-acbd-f7ed8b5587ea',
    beneficiaryAddress: {
      addressLine1: 'af8a46e8-905b-4b45-9fec-0d48607e42b7',
      addressLine2: 'aff5bf2c-e9dd-4be8-8098-af71c13bda69',
      postalCode: '64b15c71-bc3d-4f63-ad2a-e9b843c65c92',
      city: 'b4b8ae70-32ee-4437-80db-f026ce0c0735',
      country: '7b3090cf-d983-41e5-a77d-1360fe764763',
    },
    telephone: 'f4793800-0da8-44d1-9e12-eb783dbf475e',
    telefax: '52d82c01-f77e-4ebf-8ecf-a73e7cd94b9c',
    email: '748395ea-c6bb-4300-9019-8183a21d20e8',
    website: '60f86e1d-0933-4434-8c18-69ae090d7ecb',
    paymentLink: '8bf936de-56fd-4a32-b1dd-e14ca3ae5087',
  },
  showPaymentLink: '0f3fdb9c-2eee-4f62-a981-aeda7851e5eb',
  showBankDetails: '60f1fc06-c8f6-4c5e-86f1-6c41b93b070b',
  isNet: 'a8217c4e-a81a-40f2-b374-a930d331fef3',
}
